import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchSiteInfo, fetchImageBySection, fetchSocialMedia } from '../services/api';
import { Mail, MapPin, Heart } from 'lucide-react';
import { FaInstagram } from 'react-icons/fa';

const Footer = () => {
  const [siteInfo, setSiteInfo] = useState(null);
  const [footerLogo, setFooterLogo] = useState(null);
  const [socialLinks, setSocialLinks] = useState({});
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const loadData = async () => {
      try {
        const [info, logo, social] = await Promise.all([
          fetchSiteInfo(),
          fetchImageBySection('footer-logo'),
          fetchSocialMedia()
        ]);
        setSiteInfo(info);
        if (logo) setFooterLogo(logo.url);
        if (social) setSocialLinks(social);
      } catch (error) {
        console.error('Error loading footer data:', error);
      }
    };
    loadData();
  }, []);

  return (
    <footer className="bg-black border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <img 
              src={footerLogo} 
              alt="Lady Taveenah" 
              className="h-16 md:h-20 w-auto mb-6"
              style={{ display: footerLogo ? 'block' : 'none' }}
            />
            <p className="text-white/50 text-sm leading-relaxed">
              Professional discipline therapy in Vancouver, BC. 
              Transformative experiences in a safe, elegant environment.
            </p>
            
            {/* Social Links */}
            {(socialLinks.instagram_url || socialLinks.loyalfans_url) && (
              <div className="flex gap-4 mt-6">
                {socialLinks.instagram_url && (
                  <a 
                    href={socialLinks.instagram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-red-600 transition-colors"
                    aria-label="Instagram"
                  >
                    <FaInstagram size={18} />
                  </a>
                )}
                {socialLinks.loyalfans_url && (
                  <a 
                    href={socialLinks.loyalfans_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-red-600 transition-colors"
                    aria-label="LoyalFans"
                  >
                    <Heart size={18} />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-white text-sm uppercase tracking-wider mb-6">Navigate</h4>
            <div className="space-y-3">
              {[
                { path: '/', label: 'Home' },
                { path: '/about', label: 'About' },
                { path: '/services', label: 'Services' },
                { path: '/gallery', label: 'Gallery' },
                { path: '/contact', label: 'Contact' }
              ].map(link => (
                <Link 
                  key={link.path}
                  to={link.path} 
                  className="block text-white/50 hover:text-white text-sm transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white text-sm uppercase tracking-wider mb-6">Services</h4>
            <div className="space-y-3">
              {[
                { path: '/services', label: 'Private Sessions' },
                { path: '/services', label: 'Extended Sessions' },
                { path: '/travel', label: 'Travel' },
                { path: '/tribute-gifts', label: 'Tributes' },
                { path: '/booking', label: 'Book Now' }
              ].map((link, i) => (
                <Link 
                  key={i}
                  to={link.path} 
                  className={`block text-sm transition-colors ${
                    link.label === 'Book Now' 
                      ? 'text-red-500 hover:text-red-400' 
                      : 'text-white/50 hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white text-sm uppercase tracking-wider mb-6">Contact</h4>
            <div className="space-y-4">
              <div className="flex gap-3 text-sm">
                <Mail className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                <span className="text-white/50">{siteInfo?.email || 'contact@ladytaveenah.com'}</span>
              </div>
              <div className="flex gap-3 text-sm">
                <MapPin className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                <span className="text-white/50">Vancouver, BC</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-16 pt-8 border-t border-white/5">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/40 text-xs">
              © {currentYear} Lady Taveenah. All rights reserved.
            </p>
            <div className="flex gap-6 text-xs">
              <Link to="/privacy-policy" className="text-white/40 hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" className="text-white/40 hover:text-white transition-colors">
                Terms of Service
              </Link>
              <Link to="/disclaimer" className="text-white/40 hover:text-white transition-colors">
                Disclaimer
              </Link>
            </div>
          </div>
          <p className="text-white/30 text-xs text-center mt-6">
            18+ only. Discretion guaranteed.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
